import { Control } from "Controls/Control";
import { IDropDownControl } from "Controls/Interfaces";
import { IODataResult } from "Entities";
import { Util } from "Core";
import { TextBlockCategory } from "Enums";
import { DataRequestOption } from "Service/Client";

/**
 * @hidden
 */
export abstract class BaseDropDownControl extends Control implements IDropDownControl {
    /**
     * @hidden
     */
    protected dataSource: kendo.data.DataSource;

    /**
     * Get control ids where this control is dependent on.
     * @hidden
     */
    public getDependencyControlIds(): number[] {
        return this.controlDefinition.DependencyControlIds;
    }

    // public abstract getText(): string;

    public abstract getDataRequest(): DataRequestOption;

    public abstract setData(data: IODataResult): Promise<void>;

    // public abstract selectItemAt(index: number): void;

    public abstract containsValue(value: string): boolean;

    // public abstract selectItemWithText(text: string): void;

    public abstract setBusy(isBusy: boolean): void;

    /**
     * Gets the current amount of items in the dropdownlist.
     * @returns Item count of dropdownlist.
     * @since 4.0.0
     */
    public getItemCount(): number {
        return this.dataSource.total() - (this.controlDefinition.InsertNullValue || false ? 1 : 0);
    }

    /**
     * Returns the text column name of this control.
     * @hidden
     */
    public getTextColumnName(): string {
        return this.controlDefinition.TextColumnName;
    }

    // fields for formatting the value.
    protected lastText: string;
    protected textMatcher: RegExp;

    protected getDropDownOptions(): {
        template: Function;
        headerTemplate: string;
        setListWidth: boolean;
        listWidth: number;
        noDataTemplate: string;
    } {
        // check if multicolumn -> adding needed options

        let totalWidth = 0;
        let template: Function;
        let headerTemplate: string;
        if (this.controlDefinition.DataColumns && this.controlDefinition.DataColumns.length > 0) {
            let columnMeasurementElement = $("<div class='dms-column-width-dropdown'/>");
            this.jQueryElement.append(columnMeasurementElement);

            headerTemplate = "<div class='dms-dropdown-header k-widget k-header'>";
            let templateStr: string = "<span class='dms-dropdown-item'>";

            for (const dataColumn of this.controlDefinition.DataColumns) {
                let multiColumnItemWidth = Util.stringIsNullOrEmpty(dataColumn.Width) ? "200px" : dataColumn.Width;
                columnMeasurementElement.width(multiColumnItemWidth);
                totalWidth += columnMeasurementElement.width() + 6;
                headerTemplate += `<span style="width:${multiColumnItemWidth};padding-left:3px;padding-right:3px;">${this.textBlockService.get(
                    dataColumn.Caption || dataColumn.ColumnName,
                    this.textBlockCategory
                )}</span>`;
                templateStr += `<p style="width:${multiColumnItemWidth}">#= this.formatValue(data.get("${
                    dataColumn.ColumnName
                }")) || "" #</p>`;
            }
            templateStr += "</span>";
            headerTemplate += "</div>";

            columnMeasurementElement.remove();

            template = $.proxy(kendo.template(templateStr, { useWithBlock: false }), this);
        } else {
            // add highlighting of matched text.
            template = $.proxy(
                kendo.template(`#= this.formatValue(data.get("${this.controlDefinition.CommandTextColumnName}")) || "" #`, {
                    useWithBlock: false
                }),
                this
            );
        }

        let setListWidth = this.controlDefinition.DataColumns && this.controlDefinition.DataColumns.length > 0;
        let listWidth = 0;
        if (setListWidth) {
            listWidth = totalWidth + kendo.support.scrollbar() + 16;
        }

        let noDataTemplate = `<div class="dms-no-data"><span class="far fa-face-frown"></span>&nbsp; ${this.textBlockService.get(
            "ComboboxNoData",
            TextBlockCategory.global
        )}</div>`;

        return {
            template: template,
            headerTemplate: headerTemplate,
            setListWidth: setListWidth,
            listWidth: listWidth,
            noDataTemplate: noDataTemplate
        };
    }

    protected getFilterSource(dataTextField: string): (s: any) => void {
        return (s: any) => {
            if (!s) {
                this.dataSource.filter({});
            } else {
                let filter: kendo.data.DataSourceFilters = {};
                filter.logic = "or";
                filter.filters = [];

                if (this.controlDefinition.DataColumns && this.controlDefinition.DataColumns.length > 0) {
                    for (let i = 0, len = this.controlDefinition.DataColumns.length; i < len; i++) {
                        let dataColumn = this.controlDefinition.DataColumns[i];
                        filter.filters.push({
                            field: dataColumn.ColumnName,
                            operator: "contains",
                            value: s.value
                        });
                    }
                } else {
                    filter.filters.push({
                        field: dataTextField,
                        operator: "contains",
                        value: s.value
                    });
                }
                this.dataSource.filter(filter);
            }
        };
    }

    /**
     * Get the display template for grid cells.
     * @param bindingField Name of field.
     * @hidden
     */
    public getDisplayTemplate(bindingField?: string): string {
        return `#= get("${this.controlDefinition.TextColumnName}") == null ? "${this.controlDefinition.ReplaceNullValue || ""}" : get("${
            this.controlDefinition.TextColumnName
        }") #`;
    }

    /**
     * Get the template for the grid group header.
     * @hidden
     */
    public getGroupHeaderTemplate(): string {
        return super.getDisplayTemplate("value");
    }

    /**
     * Get the display template for a binding field.
     * @hidden
     */
    public getDisplayTemplateForField(bindingField: string): string {
        return super.getDisplayTemplate(bindingField);
    }
}
